/**
 * Flatas hoeyde naar tastaturet er oppe.
 *
 * iOS-en i felt overser `interactive-widget=resizes-content`: layout-
 * viewporten krymper ikke naar tastaturet kommer, bare den visuelle. Flata er
 * festet til layout-viewporten og maa derfor faa hoeyden sin herfra.
 *
 * Alt som skriver `--vvh` gaar gjennom denne fila, fordi de to kildene —
 * anslaget i trykket og maalingen fra visualViewport — ellers kjemper om
 * variabelen midt i tastaturanimasjonen.
 */

const NOKKEL = "infoflyt.kbd.v3";

/** Tastaturets hoeyde, husket mellom oekter fordi den trengs foer maaling. */
let hoeyde = (() => {
  try {
    const n = Number(localStorage.getItem(NOKKEL));
    return Number.isFinite(n) && n > 0 ? n : 0;
  } catch {
    return 0;
  }
})();

/**
 * Holder flata i ro mens iOS roter med viewporten.
 *
 * Maalt paa enhet, fire kjoeringer paa rad: naar skrivefeltet faar fokus,
 * krymper iOS layout-viewporten et oeyeblikk — `window.innerHeight` gikk
 * 797 → 664, 732, 684, 615 — og ruller noeyaktig like mye. `Y = 797 - IH` i
 * hver eneste av dem, og flatas overkant `A = -Y`. Et par bilder senere er alt
 * tilbake til 797 og 0. Beloepet er vilkaarlig og varierer fra post til post.
 *
 * Foerste forsoek nullet bare rullingen. Det fjernet utrykket oppover, men
 * innfoerte ett nedover: flata rykket opp N piksler og ble dratt tilbake
 * 15-30 ms etter, og den tilbakestillingen er i seg selv en bevegelse. At N
 * varierte er grunnen til at hoppet saa forskjellig ut hver gang.
 *
 * Vakta nuller rullingen etterpaa. Den er opprydding, ikke virkemiddel:
 * rullingen hindres ved kilden med `focus({ preventScroll: true })`.
 */
let stoppVakt: (() => void) | null = null;
let planlagt = false;

/*
 * `top`-kompensasjonen er fjernet.
 *
 * Den forsoekte aa motvirke iOS sin forskyvning bilde for bilde, med `scrollY`,
 * saa med summen av `scrollY` og `offsetTop`, saa med `pageTop`. Ingen av dem
 * fjernet symptomet, og minst én av dem skjoev flata to hundre punkter nedover.
 * Aa kompensere for en bevegelse er feil grep naar bevegelsen kan hindres: se
 * `fokuserUtenRulling` i LoggScreen, som slaar av rullingen ved kilden.
 *
 * Vakta under staar igjen som opprydding, ikke som virkemiddel.
 */

function startVakt(): void {
  if (stoppVakt !== null) return;

  const paa = (): void => {
    if (window.scrollY <= 0 || planlagt) return;
    planlagt = true;
    requestAnimationFrame(() => {
      planlagt = false;
      // I neste bilde, ikke synkront: et `scrollTo` midt inne i iOS sin egen
      // rulleanimasjon lager en dragkamp som spretter forbi null.
      if (window.scrollY > 0) window.scrollTo(0, 0);
    });
  };
  window.addEventListener("scroll", paa, { passive: true });
  window.visualViewport?.addEventListener("scroll", paa);
  window.visualViewport?.addEventListener("resize", paa);
  stoppVakt = () => {
    window.removeEventListener("scroll", paa);
    window.visualViewport?.removeEventListener("scroll", paa);
    window.visualViewport?.removeEventListener("resize", paa);
    stoppVakt = null;
    planlagt = false;
  };
  paa();
}

/** Satt mens tastaturet glir opp. Da aksepteres bare lavere verdier. */
let krymperTil = 0;
/** Satt mens tastaturet glir ned. Da ignoreres maalingen helt — maalet er kjent. */
let vokser = false;
let slippTimer: number | undefined;
let naa = 0;

/**
 * Setter flatas hoeyde. Alltid momentant, og uten at noe annet beveger seg.
 *
 * Hoeyden kan ikke animeres med en CSS-overgang: en overgang gjoer at den
 * BRUKTE hoeyden fortsatt er den gamle i det oeyeblikket skrivefeltet faar
 * fokus, og det er nettopp da iOS avgjoer om den skal panorere for aa faa
 * feltet fram over tastaturet. Da var forhaandshoeyden uten virkning, og
 * toppstripa gled ut.
 *
 * Og skiftet kan ikke mykes opp med en transform paa `.pager` heller, selv om
 * toppstripa ligger utenfor den. Skrivefeltet ligger inni `.pager`, saa en
 * `translateY` flytter nettopp det elementet iOS maaler — og da panorerer den
 * for aa «redde» et felt den tror ligger under tastaturet. Maalt paa enhet:
 * flata krympet riktig, og et oeyeblikk senere ble hele appen skjoevet ut.
 *
 * Det finnes ikke noe element mellom toppstripa og skrivefeltet aa animere.
 * Derfor skifter flata hoeyde i ett hopp, med vilje.
 */
function settHoeyde(h: number): void {
  naa = h;
  document.documentElement.style.setProperty("--vvh", `${h}px`);
}

/**
 * Gir plass til tastaturet med én gang.
 *
 * MAA kalles i selve trykket, foer React rendrer. Skrivefeltet monteres med
 * autoFocus, og iOS avgjoer om siden skal rulles for aa faa det fokuserte
 * feltet fram i det oeyeblikket fokus settes. Kom hoeyden etterpaa — i en
 * layout-effekt, som er saa tidlig React kan tilby — hadde iOS alt bestemt
 * seg, og da panorerte den: flata er `position: fixed`, altsaa festet til
 * vinduet, saa toppstripa gled ut av skjermen og kom ikke tilbake.
 *
 * 42 prosent er et norsk iPhone-tastatur med forslagsrad. Anslaget brukes bare
 * foer foerste maaling noensinne; etter det er verdien den telefonen faktisk
 * har.
 */
export function gjoerPlass(): void {
  const anslag = hoeyde > 0 ? hoeyde : Math.round(window.innerHeight * 0.42);
  const maal = Math.max(240, window.innerHeight - anslag);
  if (maal >= naa) return;
  vokser = false;
  krymperTil = maal;
  startVakt();
  settHoeyde(maal);
  /*
   * Laasen slippes etter at tastaturet er framme. Da vinner maalingen igjen,
   * og et anslag som var noen piksler feil rettes opp — uten at mellomverdiene
   * underveis fikk dra hoeyden opp og ned.
   */
  window.clearTimeout(slippTimer);
  slippTimer = window.setTimeout(() => {
    krymperTil = 0;
    const vv = window.visualViewport;
    if (vv != null) lesViewport(vv);
  }, 420);
}

/**
 * Tar plassen tilbake med én gang posten er ferdig.
 *
 * Motstykket til `gjoerPlass`, og noedvendig av samme grunn. Uten dette var
 * lukkingen reaktiv mens aapningen var forutseende: vi ventet paa at iOS skulle
 * melde at tastaturet var borte, og foerst DA startet flatas egne 340 ms. De to
 * bevegelsene laa altsaa etter hverandre i stedet for oppaa hverandre, og
 * feltet kom drygt et halvt sekund for sent.
 *
 * Settes hoeyden i det posten avsluttes, vokser flata i takt med at tastaturet
 * glir ned — samme maate som den krymper i takt med at det kommer opp.
 */
export function giTilbakePlass(): void {
  if (naa >= window.innerHeight) return;
  vokser = true;
  krymperTil = 0;
  settHoeyde(window.innerHeight);
  window.clearTimeout(slippTimer);
  // Naar tastaturet er nede slipper vi maalingen til igjen, slik at annen krom
  // — adresselinje, hjemmestripe — fortsatt kan justere hoeyden.
  slippTimer = window.setTimeout(() => {
    vokser = false;
    const vv = window.visualViewport;
    if (vv != null) lesViewport(vv);
  }, 420);
}

function lesViewport(vv: VisualViewport): void {
  /*
   * Rundes ned. iOS gir brudne piksler, og en flate en halv piksel for hoey
   * legger en haarfin stripe bakgrunn under tastaturet.
   */
  const h = Math.floor(vv.height);
  const kbd = window.innerHeight - h;
  // Lave verdier er annen krom — adresselinje, hjemmestripe — ikke tastatur.
  if (kbd > window.innerHeight * 0.25) {
    hoeyde = kbd;
    try {
      localStorage.setItem(NOKKEL, String(kbd));
    } catch {
      // Privat modus kan nekte lagring. Da gjelder anslaget hver oekt.
    }
  }
  // Tastaturet er nede igjen: da skal loggen kunne blas som vanlig, uten at
  // noe overstyrer rullingen.
  if (kbd <= 8) stoppVakt?.();

  /*
   * Mens flata vokser er maalet alt kjent — full hoeyde — og satt. iOS sender
   * mellomverdier mens tastaturet glir ned, og aa foelge dem ville dratt flata
   * tilbake ned igjen bilde for bilde.
   */
  if (vokser) return;

  // Mens laasen staar: bare krymp. Ellers rykker hoeyden opp og ned i takt
  // med mellomverdiene iOS sender mens tastaturet glir opp.
  if (krymperTil > 0) {
    if (h >= krymperTil) return;
    krymperTil = h;
  }
  settHoeyde(h);
}

/** Kobler flatas hoeyde til den visuelle viewporten. Returnerer opprydding. */
export function foelgViewport(): () => void {
  const vv = window.visualViewport;
  if (vv == null) return () => undefined;
  const paa = (): void => lesViewport(vv);
  paa();
  vv.addEventListener("resize", paa);
  // Rotasjon endrer innerHeight uten noen resize paa den visuelle.
  window.addEventListener("orientationchange", paa);
  return () => {
    vv.removeEventListener("resize", paa);
    window.removeEventListener("orientationchange", paa);
    window.clearTimeout(slippTimer);
  };
}
